import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe } from '@stripe/react-stripe-js';
import useAuthToken from '../../hooks/useAuthToken';
import AppConstants from '../../AppConstants';

const stripePromise = loadStripe(AppConstants.STRIPE_PUBLISHABLE_KEY || '');

type PaymentMethodSummary = {
  id: string;
  brand?: string;
  last4?: string;
};

function MakePaymentForm() {
  const stripe = useStripe();
  const { userToken, ready } = useAuthToken();

  const [methods, setMethods] = useState<PaymentMethodSummary[]>([]);
  const [loadingMethods, setLoadingMethods] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const disabled = useMemo(() => !ready || !userToken || submitting || !stripe, [ready, userToken, submitting, stripe]);

  useEffect(() => {
    const fetchMethods = async () => {
      if (!userToken) return;
      setLoadingMethods(true);
      setError(null);
      try {
        const res = await axios.get(AppConstants.PAYMENTS_LIST_METHODS_API, {
          headers: { Authorization: `Bearer ${userToken}` },
        });
        const payload = res.data;
        let list: PaymentMethodSummary[] = [];
        if (Array.isArray(payload)) list = payload as PaymentMethodSummary[];
        else if (Array.isArray(payload?.data?.paymentMethods)) list = payload.data.paymentMethods;
        else if (Array.isArray(payload?.data)) list = payload.data;
        else if (Array.isArray(payload?.paymentMethods)) list = payload.paymentMethods;
        else if (Array.isArray(payload?.methods)) list = payload.methods;
        setMethods(list);
        if (list.length > 0) setSelectedMethod(list[0].id);
      } catch (err: any) {
        const apiMessage = err?.response?.data?.message || err?.message;
        setError(apiMessage || 'Failed to load payment methods.');
      } finally {
        setLoadingMethods(false);
      }
    };
    if (ready && userToken) fetchMethods();
  }, [ready, userToken]);

  const onSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    setError(null);

    if (!userToken) {
      setError('Missing token. Open this page via the app link.');
      return;
    }
    if (!selectedMethod) {
      setError('Please select a payment method.');
      return;
    }
    const numericAmount = Number(amount);
    if (!numericAmount || numericAmount <= 0) {
      setError('Enter a valid amount.');
      return;
    }

    try {
      setSubmitting(true);
      const res = await axios.post(
        AppConstants.PAYMENTS_MAKE_PAYMENT_API,
        { amount: numericAmount, paymentMethodId: selectedMethod },
        { headers: { Authorization: `Bearer ${userToken}` } }
      );

      const { clientSecret, status, message: apiMsg } = res.data || {};

      if (clientSecret && stripe) {
        const confirmRes = await stripe.confirmCardPayment(clientSecret);
        if (confirmRes.error) {
          setError(confirmRes.error.message || 'Payment confirmation failed.');
        } else if (confirmRes.paymentIntent && confirmRes.paymentIntent.status === 'succeeded') {
          setMessage('Payment successful.');
        } else {
          setMessage(`Payment status: ${confirmRes.paymentIntent?.status || status || 'processed'}`);
        }
      } else {
        // If backend fully captured the payment without extra confirmation
        if (status === 'succeeded') {
          setMessage('Payment successful.');
        } else {
          setMessage(apiMsg || `Payment processed with status: ${status || 'ok'}`);
        }
      }
    } catch (err: any) {
      const apiMessage = err?.response?.data?.message || err?.message;
      setError(apiMessage || 'Payment failed.');
    } finally {
      setSubmitting(false);
    }
  }, [userToken, selectedMethod, amount, stripe]);

  return (
    <div style={{ maxWidth: 520, margin: '40px auto', padding: 16 }}>
      <h2>Make Payment</h2>
      {!ready ? (
        <p>Initializing…</p>
      ) : !userToken ? (
        <p style={{ color: '#b00' }}>Missing token. Please access via the app link.</p>
      ) : (
        <form onSubmit={onSubmit}>
          <div style={{ marginBottom: 12 }}>
            <label>Amount</label>
            <input
              type="number"
              min="1"
              step="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              style={{ width: '100%', padding: 8, marginTop: 6 }}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label>Payment Method</label>
            <select
              value={selectedMethod}
              onChange={(e) => setSelectedMethod(e.target.value)}
              disabled={loadingMethods}
              style={{ width: '100%', padding: 8, marginTop: 6 }}
            >
              {(!Array.isArray(methods) || methods.length === 0) && <option value="">No saved methods</option>}
              {(Array.isArray(methods) ? methods : []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.brand ? m.brand.toUpperCase() : 'Card'} •••• {m.last4 || m.id}
                </option>
              ))}
            </select>
          </div>

          <button type="submit" disabled={disabled || methods.length === 0}>
            {submitting ? 'Processing…' : 'Pay'}
          </button>

          {message && <p style={{ color: 'green', marginTop: 12 }}>{message}</p>}
          {error && <p style={{ color: 'crimson', marginTop: 12 }}>{error}</p>}

          <div style={{ marginTop: 16 }}>
            <a href="/add-payment-method">Add another payment method</a>
          </div>
        </form>
      )}
    </div>
  );
}

export default function MakePaymentPage() {
  return (
    <Elements stripe={stripePromise}>
      <MakePaymentForm />
    </Elements>
  );
}
